import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { Line as ChartLine } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

interface TripsPerHourChartProps {
  data: { hour: string; count: number }[];
}

// Pure helper — no component closure deps, safe at module scope
function formatTimeLabel(hourString: string): string {
  if (!hourString) return '';
  const parts = hourString.split(' ');
  if (parts.length > 1) {
    return parts[parts.length - 1];
  }
  return hourString;
}

const TripsPerHourChart: React.FC<TripsPerHourChartProps> = ({ data }) => {
  const { t } = useTranslation('simulation');
  const chartOptions = useMemo(
    () => ({
      responsive: true,
      plugins: {
        legend: { display: false },
        title: { display: true, text: t('results.charts.tripsPerHourTitle'), font: { size: 16 } },
        tooltip: {
          mode: 'index' as const,
          intersect: false,
        },
      },
      scales: {
        x: { title: { display: true, text: t('results.charts.hourAxis') }, ticks: { maxRotation: 45, minRotation: 45 } },
        y: { title: { display: true, text: t('results.charts.requestsAxis') }, beginAtZero: true },
      },
    }),
    [t]
  );
  const chartData = useMemo(
    () => ({
      labels: data.map((d) => formatTimeLabel(d.hour)),
      datasets: [
        {
          label: t('results.charts.tripRequestsLegend'),
          data: data.map((d) => d.count),
          fill: false,
          borderColor: '#1976d2',
          backgroundColor: '#1976d2',
          tension: 0.2,
          pointRadius: 2,
        },
      ],
    }),
    [data, t],
  );

  return (
    <Box sx={{ width: 420, height: 220, background: 'white', borderRadius: 2, p: 1 }}>
      <ChartLine data={chartData} options={chartOptions} />
    </Box>
  );
};

export default React.memo(TripsPerHourChart);
